import React from "react";
import {
  Box, TableRow, TableCell, Typography, Button, Select, MenuItem, Chip
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

const cellBase = {
  fontSize: "12px",
  borderColor: "#e7edf6",
  py: 1.25,
  px: 1,
};

const STATUS_STYLES = {
  ACTIVO: { solid: "#0059f6", bg: "rgba(255,255,255,0.82)", text: "#06185f" },
  VENTA: { solid: "#96c87c", bg: "rgba(150, 200, 124, 0.20)", text: "#2f8f3a" },
  BAJA: { solid: "#fd8d82", bg: "rgba(253, 141, 130, 0.20)", text: "#e34d43" },
  FUTURO: { solid: "#b1b5c2", bg: "rgba(177, 181, 194, 0.22)", text: "#7d8495" },
};

const getRowBackgroundColor = (estado, derechaBg, bodyBg) => {
  switch (estado) {
    case "VENTA":  return "rgba(150, 200, 124, 0.18)";
    case "BAJA":   return "rgba(253, 141, 130, 0.18)";
    case "FUTURO": return "rgba(177, 181, 194, 0.18)";
    case "ACTIVO":
    default:       return bodyBg || derechaBg;
  }
};

const isRowDisabled = (estado) => ["VENTA", "BAJA", "FUTURO"].includes(estado);
const getStatusStyle = (estado) => STATUS_STYLES[estado] || STATUS_STYLES.ACTIVO;

const estadioActualFromRow = (r) => r?.estadioActual ?? r?.estadio ?? "INICIO";
const idFromRow = (r) => r?.id_ven_cli ?? r?.id;
const clienteIdFromRow = (r) => r?.id_cliente ?? r?.cliente?.id ?? r?.cliente_id ?? r?.clienteId;

export default function VendedorTableRow({
  row,
  ui,
  readOnly,
  edgeHit = false,
  estadoDisabled = false,
  showVendedorColumn = false,
  vendedores = [],
  selected,
  onSelect,
  onEstadoChange,
  onOpenSeguimiento,
  onDropToEstadio,
  onOpenCliente,
  onReassignVendedor,
}) {
  const { ESTADIOS, ESTADOS, COLORS, WIDTHS } = ui;
  const rowId = idFromRow(row);
  const currentEstado = row?.estadoVenta ?? row?.estado ?? "ACTIVO";
  const statusStyle = getStatusStyle(currentEstado);
  // rowDisabled: disables interactions like drag/drop, selecting row, hover effects
  const rowDisabled = readOnly ? true : isRowDisabled(currentEstado);
  // selectDisabled: the Estado <Select> should be enabled when estado is ACTIVO
  // even if the table is in readOnly mode. For other estados (VENTA/BAJA/FUTURO)
  // the select remains disabled.
  const selectDisabled = isRowDisabled(currentEstado) || (readOnly && currentEstado !== "ACTIVO");
  const rowBg = getRowBackgroundColor(currentEstado, COLORS.derechaBg, COLORS.bodyBg);
  const closedRow = isRowDisabled(currentEstado);
  const estadioActual = estadioActualFromRow(row);
  const currentVendedorId = row?.id_vendedor ?? row?.vendedor_id ?? row?.vendedorAnteriorId ?? "";

  const clienteNombre =
    row?.clienteNombre ??
    row?.nombreCliente ??
    `Cliente #${row.id_cliente ?? clienteIdFromRow(row)}`

  const consulta =
    row?.consulta ??
    row?.cliente?.consulta ??
    null

  const consulta2 =
    row?.consulta2 ??
    row?.cliente?.consulta2 ??
    null

  return (
    <TableRow
      hover={!rowDisabled}
      sx={{
        opacity: 1,
        transition: "background-color 120ms ease, transform 120ms ease, opacity 120ms ease",
        "&:hover": !rowDisabled ? {
          backgroundColor: "rgba(0, 89, 246, 0.02)",
        } : {},
        position: "relative",
      }}
    >
      {showVendedorColumn && (
        <TableCell
          align="center"
          sx={{
            ...cellBase,
            width: WIDTHS.vendedorCol || WIDTHS.estadioCol,
            maxWidth: WIDTHS.vendedorCol || WIDTHS.estadioCol,
            minWidth: WIDTHS.vendedorCol || WIDTHS.estadioCol,
            backgroundColor: rowBg,
            borderRight: "1px solid #e7edf6",
            ...(closedRow && {
              position: "relative",
              pl: 1.4,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 5,
                backgroundColor: statusStyle.solid,
              },
            }),
          }}
        >
          <Select
            size="small"
            value={currentVendedorId ? String(currentVendedorId) : ""}
            onChange={(e) => onReassignVendedor && onReassignVendedor(row, e.target.value)}
            disabled={!onReassignVendedor || vendedores.length === 0}
            displayEmpty
            renderValue={(val) => {
              const vendedor = vendedores.find((v) => String(v.id) === String(val));
              return (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "16px minmax(0, 1fr) 16px",
                    alignItems: "center",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <CompareArrowsIcon sx={{ fontSize: 12, color: "#4c5d86", justifySelf: "start" }} />
                  <Typography
                    sx={{
                      color: "#06185f",
                      fontSize: 11,
                      fontWeight: 800,
                      lineHeight: 1.15,
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {vendedor?.nombre || row.nombreVendedor || `Vendedor #${currentVendedorId || "-"}`}
                  </Typography>
                  <Box />
                </Box>
              );
            }}
            sx={{
              width: "100%",
              height: 36,
              minHeight: 36,
              backgroundColor: "rgba(255,255,255,0.82)",
              borderRadius: "8px",
              color: "#06185f",
              boxShadow: "none",
              "& .MuiSelect-select": {
                py: 0.45,
                pl: 0.9,
                pr: "20px !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiSelect-icon": {
                right: 5,
                fontSize: 18,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#dce5f2",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0,89,246,0.35)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#0059f6",
              },
            }}
          >
            <MenuItem value="" disabled>
              Sin vendedor
            </MenuItem>
            {vendedores.map((v) => (
              <MenuItem key={v.id} value={String(v.id)}>
                {v.nombre || v.nombreCompleto || `Vendedor #${v.id}`}
              </MenuItem>
            ))}
          </Select>
        </TableCell>
      )}
      
      {ESTADIOS.map((s, index) => (
        <TableCell
          key={s.key}
          align="center"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => onDropToEstadio && onDropToEstadio(s.key, e)}
          sx={{
            ...cellBase,
            width: WIDTHS.estadioCol,
            maxWidth: WIDTHS.estadioCol,
            minWidth: WIDTHS.estadioCol,
            backgroundColor: rowBg,
            borderRight: "1px solid #e7edf6",
            ...(closedRow && !showVendedorColumn && index === 0 && {
              position: "relative",
              pl: 1.4,
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 5,
                backgroundColor: statusStyle.solid,
              },
            }),
          }}
        >
          {row.estadioActual === s.key ? (
            <Button
              size="small"
              draggable={!rowDisabled}
              onDragStart={(ev) => {
                if (rowDisabled) return;
                try {
                  const from = estadioActual;
                  // make drag feel like a move
                  ev.dataTransfer.effectAllowed = "move";
                  // application/json payload
                  ev.dataTransfer.setData("application/json", JSON.stringify({ id: rowId, from }));
                  // fallback plain text (some browsers/platforms require text/plain)
                  ev.dataTransfer.setData("text/plain", String(rowId));
                } catch (err) {}
              }}
              variant={selected ? "contained" : "outlined"}
              disableElevation
              onClick={() => {
                if (rowDisabled) return;
                onSelect && onSelect();
              }}
              onDoubleClick={() => onOpenCliente && onOpenCliente(row)}
              sx={{
                textTransform: "none",
                borderColor: selected ? "#0059f6" : "#dce5f2",
                color: "#0059f6",
                backgroundColor: selected ? "rgba(0,89,246,0.10)" : "rgba(255,255,255,0.76)",
                borderRadius: "8px",
                boxShadow: selected
                  ? "inset 0 0 0 1px rgba(0,89,246,0.22), 0 8px 18px rgba(0,89,246,0.14)"
                  : "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 0.25,
                px: 0.5,
                py: 0.7,
                width: "100%",
                maxWidth: "100%",
                "&:hover": {
                  borderColor: selected ? "#0059f6" : "rgba(0,89,246,0.45)",
                  backgroundColor: selected ? "rgba(0,89,246,0.14)" : "#fff",
                },
                ...(edgeHit ? {
                  animation: "vy-shake 160ms ease-in-out",
                } : {}),
                "@keyframes vy-shake": {
                  "0%,100%": { transform: "translateX(0)" },
                  "25%":     { transform: "translateX(-2px)" },
                  "75%":     { transform: "translateX(2px)" }
                },
              }}
            >
              {/* Nombre del cliente */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: "#0059f6",
                  width: "100%",
                  whiteSpace: "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {clienteNombre}
              </Typography>
              
              {/* Consulta + marca si hay consulta2 */}
              {(consulta || consulta2) && (
                <Typography
                  variant="caption"
                  sx={{
                    width: "100%",
                    color: "#4c5d86",
                    opacity: 0.9,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflow: "visible",
                  }}
                >
                  {consulta}
                  {consulta2 ? " (**)" : ""}
                </Typography>
              )}
            </Button>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.4 }}>
              {/* vacío visual */}
            </Typography>
          )}
        </TableCell>
      ))}

      <TableCell
        align="center"
        sx={{
          ...cellBase,
          borderRight: "1px solid #e7edf6",
          backgroundColor: rowBg
        }}
      >
        <Select
          size="small"
          value={currentEstado}
          onChange={(e) => onEstadoChange && onEstadoChange(e.target.value)}
          disabled={selectDisabled || estadoDisabled}
          displayEmpty
          renderValue={(val) => (
            <Box
              component="span"
              sx={{
                color: getStatusStyle(val).text,
                fontSize: 12,
                fontWeight: 900,
                lineHeight: 1,
              }}
            >
              {val}
            </Box>
          )}
          sx={{
            width: "100%",
            height: 36,
            backgroundColor: statusStyle.bg,
            borderRadius: "8px",
            color: statusStyle.text,
            fontWeight: 900,
            "& .MuiSelect-select": {
              py: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pr: "30px !important",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: closedRow ? statusStyle.solid : "#dce5f2",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: closedRow ? statusStyle.solid : "rgba(0,89,246,0.35)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: closedRow ? statusStyle.solid : "#0059f6",
            },
            "& .MuiSvgIcon-root": {
              color: statusStyle.text,
              right: 8,
            },
            "&.Mui-disabled": {
              opacity: 1,
            },
            "& .MuiSelect-select.Mui-disabled": {
              WebkitTextFillColor: statusStyle.text,
            },
            "& .MuiSvgIcon-root.Mui-disabled": {
              color: statusStyle.text,
            },
          }}
        >
          {ESTADOS.map((e) => (
            <MenuItem key={e} value={e}>
              <Chip
                size="small"
                label={e}
                sx={{
                  minWidth: 72,
                  backgroundColor: getStatusStyle(e).bg,
                  color: getStatusStyle(e).text,
                  border: `1px solid ${getStatusStyle(e).solid}`,
                  fontWeight: 900,
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </TableCell>
        
      {/* Acciones derecha */}
      <TableCell align="center" sx={{ ...cellBase, backgroundColor: COLORS.derechaBg }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            onOpenSeguimiento?.(row);
          }}
          sx={{
            minWidth: 56,
            height: 32,
            borderRadius: "8px",
            textTransform: "none",
            fontSize: 12,
            fontWeight: 800,
            borderColor: "rgba(0,89,246,0.55)",
            color: "#0059f6",
            backgroundColor: "rgba(255,255,255,0.72)",
            "&:hover": {
              borderColor: "#0059f6",
              backgroundColor: "#fff",
            },
          }}
        >
          Ver
        </Button>
      </TableCell>
    </TableRow>
  ); 
}
